import { useMemo, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cog, KeyRound, Network, Users, Waypoints } from 'lucide-react';
import { Button, EntityCard, FilterBar, MetricCard, PageHeader, StatusBadge } from '../components/ui';
import { useAuthStore } from '../store/authStore';

type SettingsAvailability = 'available' | 'restricted';
type SettingsSort = 'name' | 'status';

interface SettingsModule {
  id: string;
  title: string;
  description: string;
  path: string;
  owner: string;
  tags: string[];
  icon: ComponentType<{ className?: string }>;
  availability: SettingsAvailability;
  restrictionNote?: string;
}

export default function SettingsCenterPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SettingsAvailability>('all');
  const [sortBy, setSortBy] = useState<SettingsSort>('name');
  const isOperationManager = !!user?.isOperationManager;

  const modules = useMemo<SettingsModule[]>(
    () => [
      {
        id: 'batch-title',
        title: 'Batch Title Settings',
        description: 'Maintain product, unit, and process code dictionaries used in deterministic batch IDs.',
        path: '/settings/batch-title',
        owner: 'Manufacturing Ops',
        tags: ['batch', 'codes', 'titles'],
        icon: Cog,
        availability: 'available',
      },
      {
        id: 'hierarchy',
        title: 'Hierarchy Configuration',
        description: 'Define role labels and levels for downstream distribution and transfer workflows.',
        path: '/hierarchy/config',
        owner: 'Distribution Ops',
        tags: ['hierarchy', 'roles', 'network'],
        icon: Waypoints,
        availability: isOperationManager ? 'restricted' : 'available',
        restrictionNote: 'Manufacturer admin access required',
      },
      {
        id: 'operations-managers',
        title: 'Operations Managers',
        description: 'Create operational logins, reset credentials, and activate or deactivate access.',
        path: '/operations-managers',
        owner: 'Security Admin',
        tags: ['users', 'operations', 'access'],
        icon: Users,
        availability: isOperationManager ? 'restricted' : 'available',
        restrictionNote: 'Manufacturer admin access required',
      },
      {
        id: 'api-access',
        title: 'API Access',
        description: 'Manage partner API keys and subscription status for QR verification integrations.',
        path: '/api-access',
        owner: 'Integration Admin',
        tags: ['api', 'keys', 'integration'],
        icon: KeyRound,
        availability: 'available',
      },
      {
        id: 'user-management',
        title: 'User Management',
        description: 'Manage user approvals, team activation states, and profile-level operational access.',
        path: '/users/manage',
        owner: 'Security Admin',
        tags: ['users', 'approvals', 'team'],
        icon: Network,
        availability: isOperationManager ? 'restricted' : 'available',
        restrictionNote: 'Manufacturer admin access required',
      },
    ],
    [isOperationManager]
  );

  const visibleModules = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const filtered = modules.filter((module) => {
      const matchesStatus = statusFilter === 'all' || module.availability === statusFilter;
      const matchesQuery =
        normalizedQuery.length === 0 ||
        module.title.toLowerCase().includes(normalizedQuery) ||
        module.description.toLowerCase().includes(normalizedQuery) ||
        module.owner.toLowerCase().includes(normalizedQuery) ||
        module.tags.some((tag) => tag.includes(normalizedQuery));

      return matchesStatus && matchesQuery;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'status') {
        if (a.availability === b.availability) {
          return a.title.localeCompare(b.title);
        }
        return a.availability === 'available' ? -1 : 1;
      }

      return a.title.localeCompare(b.title);
    });
  }, [modules, query, sortBy, statusFilter]);

  const availableCount = modules.filter((module) => module.availability === 'available').length;
  const restrictedCount = modules.length - availableCount;

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <PageHeader
        title="Settings Center"
        description="Single entry point for manufacturer configuration and access-management modules."
        badge={<StatusBadge tone="anchored">Unified Route</StatusBadge>}
      />

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard title="Settings Modules" value={modules.length} tone="blue" icon="S" />
        <MetricCard title="Available to You" value={availableCount} tone="green" icon="A" />
        <MetricCard title="Restricted Modules" value={restrictedCount} tone="yellow" icon="R" />
      </section>

      <FilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search settings by module, owner, or keyword..."
        statusOptions={[
          { value: 'all', label: 'All', count: modules.length },
          { value: 'available', label: 'Available', count: availableCount },
          { value: 'restricted', label: 'Restricted', count: restrictedCount },
        ]}
        selectedStatus={statusFilter}
        onStatusChange={(value) => setStatusFilter(value as 'all' | SettingsAvailability)}
        sortOptions={[
          { value: 'name', label: 'Sort: Name A-Z' },
          { value: 'status', label: 'Sort: Availability' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as SettingsSort)}
      />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2" aria-label="Settings modules">
        {visibleModules.map((module) => {
          const Icon = module.icon;
          const isAvailable = module.availability === 'available';

          return (
            <EntityCard
              key={module.id}
              title={
                <span className="inline-flex items-center gap-2">
                  <Icon className="h-4 w-4 text-gray-500" />
                  {module.title}
                </span>
              }
              subtitle={module.description}
              badge={
                <StatusBadge tone={isAvailable ? 'active' : 'disabled'} size="sm">
                  {isAvailable ? 'Available' : 'Restricted'}
                </StatusBadge>
              }
              meta={[
                { label: 'Owner', value: module.owner },
                { label: 'Access', value: isAvailable ? 'Open' : module.restrictionNote || 'Restricted' },
              ]}
              footer={
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant={isAvailable ? 'primary' : 'outline'}
                    disabled={!isAvailable}
                    onClick={() => navigate(module.path)}
                  >
                    {isAvailable ? 'Open Module' : 'Access Restricted'}
                  </Button>
                </div>
              }
            />
          );
        })}
      </section>
    </div>
  );
}

